import Phaser from 'phaser';
import { HEX, CSS, FONTS, getGameplayUiTheme } from '../config';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { PauseMenu } from '../ui/PauseMenu';

function formatElapsed(durationMs) {
  const seconds = Math.max(0, Math.floor(durationMs / 1000));
  const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

// Capa de interfaz exclusiva de Práctica. Está separada de la cámara del
// entrenamiento para que HUD y controles mantengan coordenadas de pantalla
// correctas en desktop, tablet y móvil.
export class PracticeHUDScene extends Phaser.Scene {
  constructor() {
    super('PracticeHUDScene');
  }

  create() {
    this.practice = this.scene.get('PracticeScene');
    this.audio = this.registry.get('audio');
    this.worldTheme = getGameplayUiTheme(this.practice?.planetId || this.registry.get('currentPlanet') || 'suma');
    if (!this.practice?.scene?.isActive()) {
      this.scene.stop();
      return;
    }

    this.ui = this.add.container(0, 0).setDepth(100);
    this.controlResets = [];
    this.pauseMenu = null;
    this.isTouch = this.hasTouchControls();
    this.practice.events.on('practice-state', this.refresh, this);
    this.practice.events.on('practice-layout', this.layout, this);
    this.scale.on('resize', this.layout, this);
    this.events.once('shutdown', this.cleanup, this);
    this.layout();
  }

  cleanup() {
    this.practice?.events?.off('practice-state', this.refresh, this);
    this.practice?.events?.off('practice-layout', this.layout, this);
    this.scale.off('resize', this.layout, this);
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
  }

  hasTouchControls() {
    return (this.sys.game.device.input?.touch)
      || (typeof window !== 'undefined'
        && (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0));
  }

  releaseControls() {
    this.controlResets.forEach(reset => reset());
    ['left', 'right', 'jump'].forEach(key => this.practice?.setControl(key, false));
    if (this.practice?.touch) this.practice.touch.jumpPressed = false;
  }

  capsule(x, y, width, height, color, radius = 16) {
    const panel = this.add.graphics();
    panel.fillStyle(0x061329, 0.87);
    panel.fillRoundedRect(x, y, width, height, radius);
    panel.lineStyle(2, color, 0.86);
    panel.strokeRoundedRect(x, y, width, height, radius);
    this.ui.add(panel);
    return panel;
  }

  text(x, y, value, size, color, origin = 0.5, family = FONTS.friendly, weight = '900') {
    const node = this.add.text(x, y, value, {
      fontFamily: family,
      fontSize: `${size}px`,
      fontStyle: weight,
      color,
    }).setOrigin(origin);
    this.ui.add(node);
    return node;
  }

  buildPauseButton(x, y, radius) {
    const color = this.worldTheme?.tint || HEX.cyan;
    const glow = this.add.circle(x, y, radius + 5, color, 0.12);
    const circle = this.add.circle(x, y, radius, 0x061329, 0.9).setStrokeStyle(2.5, color, 0.96)
      .setInteractive(new Phaser.Geom.Circle(radius, radius, Math.max(24, radius)), Phaser.Geom.Circle.Contains);
    const label = this.add.text(x, y - 1, '⏸', {
      fontFamily: FONTS.display,
      fontSize: `${Math.round(radius * 1.2)}px`,
      fontStyle: '900',
      color: CSS.white,
    }).setOrigin(0.5).setShadow(0, 2, '#020814', 3, true, true);
    this.ui.add([glow, circle, label]);
    circle.on('pointerdown', (_pointer, _x, _y, event) => {
      event?.stopPropagation?.();
      this.openPauseMenu();
    });
  }

  openPauseMenu() {
    if (this.pauseMenu || !this.practice?.pauseMission?.()) return;
    this.releaseControls();
    this.pauseMenu = new PauseMenu(this, {
      themeColor: this.worldTheme?.tint || HEX.cyan,
      onContinue: () => this.resumeFromPause(),
      onRestart: () => this.restartFromPause(),
      onExit: () => this.exitFromPause(),
    });
  }

  resumeFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.practice?.resumeMission?.();
  }

  restartFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.practice?.restartMission?.();
  }

  exitFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.practice?.exitMission?.();
  }

  // Misma implementación visual y funcional de HUDScene (Competencia).
  // Solo cambia su ubicación dentro del HUD de Práctica.
  buildMute(x, y, size, originX) {
    if (!this.audio) return;
    this.muteBtn = this.add
      .text(x, y, this.audio.muted ? '🔇' : '🔊', { fontFamily: FONTS.friendly, fontSize: `${Math.round(size + 6)}px`, color: CSS.dim })
      .setOrigin(originX, 0.5)
      .setPadding(8)
      .setInteractive({ useHandCursor: true });
    this.ui.add(this.muteBtn);
    this.muteBtn.on('pointerdown', () => {
      const muted = this.audio.toggleMute();
      PlayerData.setSettings({ muted });
      this.muteBtn.setText(muted ? '🔇' : '🔊');
    });
  }

  ctrlButton(x, y, r, glyph, color, key) {
    const circle = this.add.circle(x, y, r, color, 0.18).setStrokeStyle(2.5, color, 0.82);
    const icon = this.add.graphics();
    const size = r * 0.46;
    icon.fillStyle(0xffffff, 0.98);
    if (glyph === '◀') icon.fillTriangle(x - size, y, x + size * 0.72, y - size * 0.82, x + size * 0.72, y + size * 0.82);
    else if (glyph === '▶') icon.fillTriangle(x + size, y, x - size * 0.72, y - size * 0.82, x - size * 0.72, y + size * 0.82);
    else icon.fillTriangle(x, y - size, x - size * 0.82, y + size * 0.72, x + size * 0.82, y + size * 0.72);
    this.ui.add([circle, icon]);

    const hitRadius = Math.max(48, r + 11);
    circle.setInteractive(new Phaser.Geom.Circle(r, r, hitRadius), Phaser.Geom.Circle.Contains);
    const pointers = new Set();
    this.controlResets.push(() => {
      pointers.clear();
      if (circle.active) circle.setFillStyle(color, 0.18);
      if (icon.active) icon.setAlpha(0.98);
    });
    circle.on('pointerdown', pointer => {
      pointers.add(pointer.id);
      this.practice?.setControl(key, true);
      circle.setFillStyle(color, 0.42);
      icon.setAlpha(1);
    });
    const release = pointer => {
      pointers.delete(pointer.id);
      if (!pointers.size) {
        this.practice?.setControl(key, false);
        if (circle.active) circle.setFillStyle(color, 0.18);
        if (icon.active) icon.setAlpha(0.98);
      }
    };
    circle.on('pointerup', release);
    circle.on('pointerout', release);
    this.input.on('pointerup', release);
    this.input.on('pointerupoutside', release);
    circle.once('destroy', () => {
      this.input.off('pointerup', release);
      this.input.off('pointerupoutside', release);
    });
  }

  layout() {
    if (!this.practice?.scene?.isActive()) return;
    this.releaseControls();
    this.controlResets = [];
    this.ui.removeAll(true);
    this.isTouch = this.hasTouchControls();

    const W = this.scale.width;
    const H = this.scale.height;
    const scale = Phaser.Math.Clamp(H / 720, 0.7, 1);
    const worldTint = this.worldTheme?.tint || HEX.cyan;
    const top = 14 * scale;
    const compact = H < 560 || W < 720;
    const sideW = Phaser.Math.Clamp(178 * scale, compact ? 124 : 145, compact ? 154 : 188);
    const sideH = compact ? 54 : 64 * scale;
    const operationW = Math.min(W * 0.46, Math.max(compact ? 232 : 310, 480 * scale));
    const operationH = compact ? 62 : 76 * scale;
    const operationX = W / 2 - operationW / 2;

    this.capsule(top, top, sideW, sideH, worldTint, 16 * scale);
    // Dos líneas centradas dentro del panel: el contador nunca compite por
    // ancho ni se superpone con la etiqueta en viewport pequeños.
    this.timeLabel = this.text(top + sideW / 2, top + sideH * 0.25, '⏱ Tiempo', compact ? 10 : 13 * scale, '#dffeff', 0.5);
    this.elapsedText = this.text(top + sideW / 2, top + sideH * 0.68, '00:00', compact ? 17 : 20 * scale, '#ffe385', 0.5, FONTS.display);

    this.capsule(operationX, top, operationW, operationH, worldTint, 19 * scale);
    this.problemText = this.text(W / 2, top + operationH / 2, '', compact ? 28 : 38 * scale, CSS.white, 0.5, FONTS.display);

    const badgeW = compact ? 126 : 152 * scale;
    const badgeH = compact ? 23 : 28 * scale;
    const badgeY = top + operationH + 8 * scale;
    this.capsule(W / 2 - badgeW / 2, badgeY, badgeW, badgeH, worldTint, badgeH / 2);
    this.modeText = this.text(W / 2, badgeY + badgeH / 2, '🎯 PRÁCTICA', compact ? 10 : 13 * scale, '#ffe385', 0.5, FONTS.display);

    const statsX = W - sideW - top;
    this.capsule(statsX, top, sideW, sideH, worldTint, 16 * scale);
    const statsCenterX = statsX + sideW / 2;
    this.correctText = this.text(statsCenterX, top + sideH * 0.30, '', compact ? 10 : 15 * scale, '#dfffee', 0.5);
    this.wrongText = this.text(statsCenterX, top + sideH * 0.70, '', compact ? 10 : 15 * scale, '#ffe2e7', 0.5);

    // Mismo botón de sonido de Competencia, alineado bajo el panel de estadísticas.
    this.buildMute(statsX + sideW, top + sideH + (compact ? 18 : 20 * scale), 20, 1);
    // Misma regla que Aventura: centrado debajo del panel superior izquierdo.
    const pauseRadius = this.isTouch ? Phaser.Math.Clamp(25 * scale, 24, 27) : 22;
    const pauseGap = Phaser.Math.Clamp(12 * scale, 8, 16);
    this.buildPauseButton(
      top + sideW / 2,
      top + sideH + pauseGap + pauseRadius,
      pauseRadius,
    );

    const progressW = Math.min(W - 48, Math.max(260, 520 * scale));
    const progressH = this.isTouch ? 42 : Math.max(52, 62 * scale);
    const progressY = this.isTouch ? H - progressH - 16 : H - progressH - 22 * scale;
    this.capsule(W / 2 - progressW / 2 - 16 * scale, progressY, progressW + 32 * scale, progressH, worldTint, 16 * scale);
    this.progress = {
      x: W / 2 - progressW / 2,
      y: progressY + (this.isTouch ? 28 : 37 * scale),
      w: progressW,
      h: this.isTouch ? 6 : 12 * scale,
    };
    this.progressFill = this.add.graphics();
    this.ui.add(this.progressFill);
    this.progressText = this.text(W / 2, progressY + (this.isTouch ? 14 : 18 * scale), '', Math.max(16, 19 * scale), CSS.white, 0.5, FONTS.friendly, '800');

    if (this.isTouch) {
      // Mismas medidas y la misma altura de los controles de Competencia.
      const radius = Phaser.Math.Clamp(Math.min(W, H) * 0.095, 42, 58);
      const buttonY = H - radius - 8;
      this.ctrlButton(radius + 18, buttonY, radius, '◀', worldTint, 'left');
      this.ctrlButton(radius + 18 + radius * 2.15, buttonY, radius, '▶', worldTint, 'right');
      this.ctrlButton(W - radius - 18, buttonY, radius, '▲', worldTint, 'jump');
    }
    this.refresh();
    this.pauseMenu?.relayout();
  }

  refresh() {
    if (!this.practice || !this.problemText || !this.progress) return;
    const scene = this.practice;
    this.problemText.setText(scene.currentProblem ? `${scene.currentProblem.text} = ?` : '');
    this.elapsedText.setText(formatElapsed(scene.sessionElapsedMs || 0));
    this.correctText.setText(`✅ Correctas: ${scene.correctCount || 0}`);
    this.wrongText.setText(`❌ Incorrectas: ${scene.wrongCount || 0}`);
    this.progressText.setText(`${scene.completed || 0}/${scene.exerciseCount || 0}`);
    const ratio = scene.exerciseCount ? Phaser.Math.Clamp((scene.completed || 0) / scene.exerciseCount, 0, 1) : 0;
    this.progressFill.clear();
    if (ratio > 0) {
      this.progressFill.fillStyle(this.worldTheme?.tint || HEX.cyan, 0.96);
      this.progressFill.fillRoundedRect(this.progress.x, this.progress.y, Math.max(this.progress.h, this.progress.w * ratio), this.progress.h, this.progress.h / 2);
    }
  }
}
